import { useState } from "react";

import { FileBrowser } from "@platform/components/ui/file-browser/file-browser";
import type { FileBrowserItem } from "@platform/components/ui/file-browser/file-browser.types";

type Node = FileBrowserItem & { children?: Node[] };

const INITIAL: Node[] = [
	{
		id: "1",
		name: "Documents",
		type: "folder",
		children: [
			{ id: "1-1", name: "contract.pdf", type: "file" },
			{ id: "1-2", name: "Drafts", type: "folder", children: [] },
		],
	},
	{ id: "2", name: "Photos", type: "folder", children: [] },
	{ id: "3", name: "readme.txt", type: "file" },
];

function childrenAt(nodes: Node[], ids: string[]): Node[] {
	let cur = nodes;
	for (const id of ids) cur = cur.find((n) => n.id === id)?.children ?? [];
	return cur;
}

// Hands back the removed node too, for cut and paste.
function removeById(nodes: Node[], id: string): { nodes: Node[]; found: Node | null } {
	let found: Node | null = null;
	const walk = (list: Node[]): Node[] => {
		const out: Node[] = [];
		for (const n of list) {
			if (n.id === id) {
				found = n;
				continue;
			}
			out.push(n.children ? { ...n, children: walk(n.children) } : n);
		}
		return out;
	};
	return { nodes: walk(nodes), found };
}

function updateAt(nodes: Node[], ids: string[], fn: (kids: Node[]) => Node[]): Node[] {
	if (ids.length === 0) return fn(nodes);
	const [head, ...rest] = ids;
	return nodes.map((n) =>
		n.id === head ? { ...n, children: updateAt(n.children ?? [], rest, fn) } : n,
	);
}

type Seg = { id: string; name: string };

function pathExists(nodes: Node[], segs: Seg[]): boolean {
	let cur = nodes;
	for (const seg of segs) {
		const node = cur.find((n) => n.id === seg.id);
		if (!node || node.type !== "folder") return false;
		cur = node.children ?? [];
	}
	return true;
}

export function TestPage() {
	const [tree, setTree] = useState<Node[]>(INITIAL);
	const [history, setHistory] = useState<Seg[][]>([[]]);
	const [index, setIndex] = useState(0);
	const [loading, setLoading] = useState(false);

	const path = history[index] ?? [];
	const ids = path.map((p) => p.id);
	const items = childrenAt(tree, ids);
	// Fake the GCS round-trip so the toolbar spinner is visible after each change.
	const sync = () => {
		setLoading(true);
		setTimeout(() => setLoading(false), 700);
	};
	// Drops history entries that no longer resolve, so back and forward never land on a folder
	// that another action deleted or moved.
	const commit = (next: Node[]) => {
		setTree(next);
		sync();
		const current = history[index] ?? [];
		const valid = history.filter((segs) => pathExists(next, segs));
		const safe = valid.length ? valid : [[]];
		const ni = safe.indexOf(current);
		setHistory(safe);
		setIndex(ni < 0 ? safe.length - 1 : ni);
	};
	const edit = (fn: (kids: Node[]) => Node[]) => commit(updateAt(tree, ids, fn));
	const go = (next: Seg[]) => {
		setHistory((h) => [...h.slice(0, index + 1), next]);
		setIndex(index + 1);
	};

	return (
		<div className="flex flex-col gap-10">
			<FileBrowser
				items={items}
				path={path.map((p) => p.name)}
				loading={loading}
				onOpenFolder={(item) => go([...path, { id: item.id, name: item.name }])}
				onNavigate={(i) => go(path.slice(0, i + 1))}
				onBack={() => setIndex((i) => Math.max(0, i - 1))}
				onForward={() => setIndex((i) => Math.min(history.length - 1, i + 1))}
				canBack={index > 0}
				canForward={index < history.length - 1}
				onCreateFolder={(name) =>
					edit((kids) => [...kids, { id: crypto.randomUUID(), name, type: "folder", children: [] }])
				}
				onRename={(item, name) =>
					edit((kids) => kids.map((k) => (k.id === item.id ? { ...k, name } : k)))
				}
				onMove={(item, target) =>
					edit((kids) => {
						const moving = kids.find((k) => k.id === item.id);
						if (!moving) return kids;
						return kids
							.filter((k) => k.id !== item.id)
							.map((k) =>
								k.id === target.id ? { ...k, children: [...(k.children ?? []), moving] } : k,
							);
					})
				}
				onReorder={(item, beforeId) =>
					edit((kids) => {
						const moving = kids.find((k) => k.id === item.id);
						if (!moving) return kids;
						const rest = kids.filter((k) => k.id !== item.id);
						const found = beforeId == null ? -1 : rest.findIndex((k) => k.id === beforeId);
						const at = found < 0 ? rest.length : found;
						return [...rest.slice(0, at), moving, ...rest.slice(at)];
					})
				}
				onPaste={(item) => {
					const { nodes, found } = removeById(tree, item.id);
					if (!found) return;
					commit(updateAt(nodes, ids, (kids) => [...kids, found]));
				}}
				onDelete={(item) => edit((kids) => kids.filter((k) => k.id !== item.id))}
				onUpload={(files) =>
					edit((kids) => [
						...kids,
						...Array.from(files).map((f) => ({
							id: crypto.randomUUID(),
							name: f.name,
							type: "file" as const,
						})),
					])
				}
			/>
		</div>
	);
}
